// src/shared/contexts/SelectionContext.tsx

import { createContext, useContext, useCallback, useState, useMemo, type ReactNode } from 'react';

interface SelectionState {
  selectedIds: Set<number>;
  lastSelectedIndex: number | null;
}

interface SelectionContextValue {
  selectedIds: Set<number>;
  selectedCount: number;
  isSelected: (id: number) => boolean;
  selectOnly: (id: number, index: number) => void;
  toggle: (id: number, index: number) => void;
  toggleRange: (id: number, index: number, allIds: number[]) => void;
  selectAll: (ids: number[]) => void;
  clearSelection: () => void;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

interface SelectionProviderProps {
  children: ReactNode;
}

export function SelectionProvider({ children }: SelectionProviderProps) {
  const [state, setState] = useState<SelectionState>({
    selectedIds: new Set(),
    lastSelectedIndex: null,
  });

  const isSelected = useCallback(
    (id: number) => state.selectedIds.has(id),
    [state.selectedIds]
  );

  // Select only this item (clear all others)
  const selectOnly = useCallback((id: number, index: number) => {
    setState({
      selectedIds: new Set([id]),
      lastSelectedIndex: index,
    });
  }, []);

  // Toggle without clearing others (Ctrl+click behavior)
  const toggle = useCallback((id: number, index: number) => {
    setState(prev => {
      const next = new Set(prev.selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { selectedIds: next, lastSelectedIndex: index };
    });
  }, []);

  // Range selection (Shift+click behavior)
  const toggleRange = useCallback((id: number, index: number, allIds: number[]) => {
    setState(prev => {
      const lastIndex = prev.lastSelectedIndex ?? index;
      const start = Math.min(lastIndex, index);
      const end = Math.max(lastIndex, index);

      const next = new Set(prev.selectedIds);
      for (let i = start; i <= end; i++) {
        next.add(allIds[i]);
      }

      return { selectedIds: next, lastSelectedIndex: index };
    });
  }, []);

  const selectAll = useCallback((ids: number[]) => {
    setState({
      selectedIds: new Set(ids),
      lastSelectedIndex: null,
    });
  }, []);

  const clearSelection = useCallback(() => {
    setState({
      selectedIds: new Set(),
      lastSelectedIndex: null,
    });
  }, []);

  const value = useMemo((): SelectionContextValue => ({
    selectedIds: state.selectedIds,
    selectedCount: state.selectedIds.size,
    isSelected,
    selectOnly,
    toggle,
    toggleRange,
    selectAll,
    clearSelection,
  }), [state.selectedIds, isSelected, selectOnly, toggle, toggleRange, selectAll, clearSelection]);

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection(): SelectionContextValue {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within SelectionProvider');
  }
  return context;
}
